<script setup lang="ts">
import { cloneDeep } from 'lodash';
import { nextTick, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
import {
  CurrentCaseServiceInterface,
  DiffListComponent,
  getContainer,
  Helpers,
  LoggerInterface,
  NamedBindings,
  TranslationService,
  UserService,
} from '@dandelion/core';
import ReadmissionUserDialog from './ReadmissionUserDialog.vue';
import { ImportedData } from '../entities/ImportedData';
import { Messages } from '../enum/Messages';
import { NamedServices } from '../enum/NamedServices';
import { NamedStorages } from '../enum/NamedStorages';
import { Preferences } from '../enum/Preferences';
import {
  AdmissionIdAndHash,
  ImportHashCacheStorage,
} from '../repository/ReadmissionChainForPatientKeyStorage';
import { ImportHashRestStorage } from '../repository/ReadmissionChainForPatientKeyRestStorage';
import { ImporterService } from '../services/ImporterService';
import { ReadmissionManagerRunner } from '../services/ReadmissionManagerRunner';
import { ImportChoice, ImportChoiceModel } from '../entities/ImportChoice';
import { ReadmissionStorageInterface } from '../interfaces/ReadmissionStorageInterface';
import { PreferencesService } from '../services/PreferencesService';
import { ImporterMode } from '../enum/ImporterMode';
import { Crypt } from '../utils/Crypt';

//TODO: pick this services by caseType
const importerService = getContainer().get(
  NamedServices.ImporterService
) as ImporterService;
const currentCaseService = getContainer('Admission').get(
  NamedBindings.CurrentCaseService
) as CurrentCaseServiceInterface;
const ts = getContainer().get(
  NamedBindings.TranslationService
) as TranslationService;
const logger = getContainer().get(NamedBindings.Logger) as LoggerInterface;
const useLocalStorage =
  import.meta.env.VITE_APP_LOCAL_STORAGE &&
  import.meta.env.VITE_APP_LOCAL_STORAGE == 'true';
const importerHashStorage: ImportHashCacheStorage | ImportHashRestStorage =
  useLocalStorage ? new ImportHashCacheStorage() : new ImportHashRestStorage();
const importedDataList: Ref<ImportedData[]> = ref([]);
const listColumn = [
  'Case Id',
  'Name',
  'Surname',
  'ICU Admission Date',
  'Import date',
  'Import user',
];
const showDiffDialog = ref(false);
const importSpinner = ref(false);
const saveimportSpinner = ref(false);
const showCloseConfirm = ref(false);
const showNoDiffMessage = ref(false);
const showLoadingImport = ref(false);
let loadImportInterval: string | number | NodeJS.Timeout | undefined;
let importedDataModel: Record<string, any>;
let currentModel: Record<string, any>;
let currentCrfVersions: Map<string, string>;
const loadingImportDialogRef = ref<HTMLDialogElement | null>(null);
const diffDialogRef = ref<HTMLDialogElement | null>(null);
const closeConfirmDialogRef = ref<HTMLDialogElement | null>(null);

const model = importerService.clonedModel;
const key: symbol = ImporterService.key;

watch(importerService.showUnchangeds, async () => {
  const display =
    importerService.showUnchangeds.value == true ? 'block' : 'none';
  await nextTick();
  checkForEmptyDivs(display);
});

const handleDialog = (
  state: Ref<boolean>,
  dialogRef: Ref<HTMLDialogElement | null>
) => {
  if (dialogRef.value) {
    state.value ? dialogRef.value.showModal() : dialogRef.value.close();
  }
};

watch(showDiffDialog, () => handleDialog(showDiffDialog, diffDialogRef));
watch(showLoadingImport, () =>
  handleDialog(showLoadingImport, loadingImportDialogRef)
);
watch(showCloseConfirm, () =>
  handleDialog(showCloseConfirm, closeConfirmDialogRef)
);

const loadImportedCaseList = async () => {
  importedDataList.value = await importerService.getList();
};

onMounted(() => {
  loadImportedCaseList();
  loadImportInterval = setInterval(
    () => loadImportedCaseList(),
    1000 * 60 * 0.5
  );
});

onUnmounted(() => {
  if (loadImportInterval) {
    clearInterval(loadImportInterval);
  }
});

const deleteImportedCase = async (caseID: string) => {
  await importerService.deleteData(caseID);
  importedDataList.value = importedDataList.value.filter(
    (data: ImportedData) => data.caseID !== caseID
  );
};

function checkForEmptyDivs(display: string) {
  const fieldsets = diffDialogRef.value!.querySelectorAll('fieldset');
  const unchangedVariables = diffDialogRef.value!.querySelectorAll(
    '.variable-unchanged'
  );
  unchangedVariables.forEach(
    (v: Element) => ((v as HTMLDivElement).style.display = display)
  );
  fieldsets.forEach((fieldset: HTMLFieldSetElement) => {
    const childDiv = fieldset.querySelectorAll('.variable-diff');
    if (childDiv && childDiv.length === 0) {
      fieldset.style.display = display;
    }
  });
  const dandelionCMP = diffDialogRef.value!.querySelector(
    '.dandelion-cmp'
  ) as HTMLElement;
  if (dandelionCMP && dandelionCMP.clientHeight === 0) {
    showNoDiffMessage.value = true;
  }
}

async function importCaseFlow(importedData: ImportedData) {

  if (importSpinner.value) return; // prevents double clicks immediately
  importSpinner.value = true;      // disable instantly

  try {
    const container = getContainer();
    const readmissionStorage = container.get(
      NamedStorages.ReadmissionStorage
    ) as ReadmissionStorageInterface;
    const centrePreferencesService = container.get(
      NamedServices.CentrePreferencesService
    ) as PreferencesService;
    const importType = (
      await centrePreferencesService.getPreference(Preferences.TYPEIMPORT)
    )?.value;
    const caseAlreadyExists =
      await importerService.checkIfAlreadyImported(importedData);
    const readmissionManagerRunner = new ReadmissionManagerRunner(
      readmissionStorage,
      importedData.caseID,
      importedData.data
    );
    const patientKey = importedData.data.patientKey;
    if (caseAlreadyExists) {
      if (importType === ImporterMode.WITH_CHOICE) {
        await currentCaseService.open(importedData.caseID);
        currentModel = cloneDeep(currentCaseService.getModel());
        currentCrfVersions = cloneDeep(currentCaseService.getCrfVersions());
        importedDataModel = cloneDeep(importedData.data);
        importerService.isImporting.value = true;
        showDiffDialog.value = true;
        setTimeout(() => checkForEmptyDivs('none'), 500);
      } else if (importType === ImporterMode.WITHOUT_CHOICE) {
        await importerService.importUpdatedData(importedData);
      }
    } else if (patientKey) {
      const patientKeyCache = await importerHashStorage.getCache();
      const hashAndLastAdmissionId = patientKeyCache.get(patientKey);

      if (hashAndLastAdmissionId) {
        // if we have an admission with the same admission id in the cache
        const lastAdmissionHistory = (
          await readmissionManagerRunner.getReadmissionHypothesis(
            hashAndLastAdmissionId.hash
          )
        )?.find(
          (history) =>
            history.caseId === hashAndLastAdmissionId.lastAdmissionIdFromChain
        );
        if (lastAdmissionHistory) {
          await readmissionManagerRunner.updateAdmissionsChain(
            lastAdmissionHistory
          );
        }
        await importerService.importNewData(
          importedData,
          patientKey,
          hashAndLastAdmissionId
        );
      } else {
        await readmissionManagerRunner.checkReadmissionsHypothesis(
          Array.from(patientKeyCache.values())
        );
        await readmissionManagerRunner.waitForDialogToClose();

        const userService = getContainer().get(
          NamedBindings.UserService
        ) as UserService;
        const variablesForHash = {
          name: importedData.data.core?.personalData?.name,
          surname: importedData.data.core?.personalData?.surname,
          birthDate: importedData.data.core?.personalData?.birthDate,
          centreCode: userService.getCurrentUser()?.value?.centre || '*',
        };

        const cryptedHash = await Crypt.generateHash(variablesForHash);
        const newHashAndLastAdmissionId = new AdmissionIdAndHash();
        newHashAndLastAdmissionId.hash = cryptedHash;
        newHashAndLastAdmissionId.lastAdmissionIdFromChain =
          importedData.caseID;
        await importerService.importNewData(
          importedData,
          patientKey,
          newHashAndLastAdmissionId
        );
      }
    } else {
      await readmissionManagerRunner.checkReadmissionsHypothesis();
      await readmissionManagerRunner.waitForDialogToClose();
      await importerService.importNewData(importedData);
    }
    importedDataList.value = importedDataList.value.filter(
      (data: ImportedData) => data.caseID !== importedData.caseID
    );
  } catch (e: any) {
    logger.logError(e, {});
  } finally {

    importSpinner.value = false; // ALWAYS reset

  }
}

function closeDiff() {
  showDiffDialog.value = false;
  showNoDiffMessage.value = false;
  importerService.isImporting.value = false;
  saveimportSpinner.value = false;
}

function setAll(
  currentVariable: ImportChoiceModel,
  fullName: string,
  status: ImportChoice
) {
  if (isImportChoice(currentVariable)) {
    Helpers.setObjByPath(fullName, model[key], status);
  } else {
    Object.keys(currentVariable).forEach((k) =>
      setAll(
        currentVariable[k],
        (fullName !== '' ? fullName + '.' : '') + k,
        status
      )
    );
  }
}

function mantainAll(currentVariable: ImportChoiceModel, fullName: string) {
  setAll(currentVariable, fullName, 'Existing');
}

function importAll(currentVariable: ImportChoiceModel, fullName: string) {
  setAll(currentVariable, fullName, 'Imported');
}

function clearAll(currentVariable: ImportChoiceModel, fullName: string) {
  setAll(currentVariable, fullName, 'NotChosen');
}

async function saveImportedData() {

  saveimportSpinner.value = true;  
  if (checkBeforeSave()) {
    await importerService.confirmUpdatedDataAfterDiff();
    closeDiff();
  }
}

function findIfNotChosen(currentVariable: ImportChoiceModel): boolean {
  if (isImportChoice(currentVariable)) {
    return currentVariable == 'NotChosen';
  } else {
    return Object.keys(currentVariable).some((k) =>
      findIfNotChosen(currentVariable[k])
    );
  }
}

function isImportChoice(
  currentVariable: ImportChoiceModel
): currentVariable is ImportChoice {
  return (
    typeof currentVariable === 'string' &&
    (currentVariable === 'Existing' ||
      currentVariable === 'Imported' ||
      currentVariable === 'NotChosen')
  );
}

function checkBeforeSave(): boolean {
  if (!findIfNotChosen(model[key])) {
    return true;
  } else {
    logger.logError(ts.t("Not all variable changes have been selected"), {});
    return false;
  }
}

function openConfirm() {
  showCloseConfirm.value = true;
  document.body.style.overflowY = 'hidden';
}

function closeConfirm() {
  showCloseConfirm.value = false;
  document.body.style.overflowY = '';
}
</script>

<template>
  <ReadmissionUserDialog></ReadmissionUserDialog>
  <section
    class="importer-case-list case-list"
    v-if="importedDataList.length > 0"
  >
    <table class="importer-table">
      <thead>
        <tr>
          <th v-for="column in listColumn" v-html="ts.t(column)"></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="importedData in importedDataList" :key="importedData.caseID">
          <td>{{ importedData.caseID }}</td>
          <td>{{ importedData.data.core?.personalData?.name }}</td>
          <td>{{ importedData.data.core?.personalData?.surname }}</td>
          <td>{{ importedData.data.core?.administrativeInfo?.icuAdmDate }}</td>
          <td>{{ importedData.metaData.timeStamp }}</td>
          <td>{{ importedData.metaData.user }}</td>
          <td>
            <button id="import-btn"  class="import-btn" :disabled="importSpinner" @click="importCaseFlow(importedData)">
        <span v-if="importSpinner" class="button-spinner" />
        <span v-else v-html="ts.t('Import')" />
            </button>
            <button class="delete-btn" :disabled="importSpinner" @click="deleteImportedCase(importedData.caseID)"
             >
           <span v-if="importSpinner" class="button-spinner" />
           <span v-else  v-html="ts.t('Delete')" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
  <dialog ref="diffDialogRef" class="diff-dialog non-modal-dialog">
    <h2 v-if="showNoDiffMessage" v-html="ts.t('No diff in the two cases')"></h2>
    <div class="content-wrapper">
      <div class="diff-dialog-buttons-wrapper">
        <button
          class="mantain-all-values-btn"
          @click="mantainAll(model[key], '')"
          v-html="ts.t('Select all existing values')"
        ></button>
        <button
          class="import-all-values-btn"
          @click="importAll(model[key], '')"
          v-html="ts.t('Select all imported values')"
        ></button>
        <button
          class="clear-all-values-btn"
          @click="clearAll(model[key], '')"
          v-html="ts.t('Clear all choices')"
        ></button>
        <button
          class="show-unchanged-btn"
          @click="importerService.toggleUnchanged()"
          v-html="
            ts.t(
              importerService.showUnchangeds.value == false
                ? 'Show unchanged variables'
                : 'Hide unchanged variables'
            )
          "
        ></button>
      </div>
      <DiffListComponent
        v-if="showDiffDialog"
        context="Admission"
        :modelToCompare="currentModel"
        :model="importedDataModel"
        :crfVersions="currentCrfVersions"
      />
      <div class="buttons-container">
        <button
          class="save-btn"
          @click="saveImportedData()"
         :disabled="saveimportSpinner"
        >
      <span v-if="saveimportSpinner" class="button-spinner" />
      <span v-else v-html="ts.t('Confirm and Save')"/>
    </button>
        <button
          class="close-btn"
          @click="openConfirm()"
          :disabled="saveimportSpinner"
          
        >
            <span v-if="saveimportSpinner" class="button-spinner" />
      <span v-else v-html="ts.t('Close')"/>
      </button>
      </div>
    </div>
  </dialog>
  <dialog ref="closeConfirmDialogRef" class="close-confirm-dialog modal-dialog">
    <h3 v-html="ts.t(Messages.CONFIRM_CLOSE_DIFF)"></h3>
    <div class="buttons-container">
      <button
        class="close-all-btn"
        @click="(closeConfirm(), closeDiff())"
        v-html="ts.t('Confirm')"
      ></button>
      <button
        class="cancel-btn"
        @click="closeConfirm()"
        v-html="ts.t('Cancel')"
      ></button>
    </div>
  </dialog>
  <dialog
    ref="loadingImportDialogRef"
    class="loading-import-dialog modal-dialog"
  >
    <div class="loading-import-dialog-content">
      <span
        class="importing-case-msg loading-msg"
        v-html="`${ts.t('Importing the case...')}`"
      />
      <img src="../../public/images/loading.gif" class="spinner" />
    </div>
  </dialog>
</template>

<style>
.importer-case-list {
  margin-top: 2rem;
}

.importer-case-list .importer-table .import-btn {
  margin-right: 0.5rem;
}

#clc .diff-dialog {
  border-radius: 1rem;
  top: 3vh;
  overflow: hidden;
}

#clc .diff-dialog section {
  padding: 0;
}

#clc .diff-dialog fieldset {
  padding: 0;
  margin: 0.5rem 0;
}

.diff-dialog .loading-diff-list-msg,
.diff-dialog .content-wrapper {
  position: relative;
  min-height: 5vw;
  min-width: 75vw;
  max-width: 92vw;
  max-height: 86vh;
  overflow: auto;
  padding: 0.5rem 1rem 1rem 0;
}

.diff-dialog .content-wrapper::-webkit-scrollbar {
  width: 1rem;
}

.diff-dialog .content-wrapper::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px #a6adbb;
  border-radius: 0.5rem;
}

.diff-dialog .content-wrapper::-webkit-scrollbar-thumb {
  background-color: #388087;
  border-radius: 0.5rem;
}

.diff-dialog .content-wrapper::-webkit-scrollbar-thumb:hover {
  background-color: #306e74;
}

.diff-dialog .diff-dialog-buttons-wrapper {
  position: absolute;
  display: flex;
  gap: 1rem;
  right: 1rem;
}

.diff-dialog .diff-dialog-buttons-wrapper button:not(:last-of-type) {
  background-color: #388087;
  color: white;
}

.diff-dialog .diff-list-component {
  min-height: 10vh;
}

.diff-dialog section > fieldset > div {
  margin: 1.5rem 0 0 2rem;
}

.diff-dialog fieldset fieldset {
  border-left: none;
  padding: 0;
  margin: 0;
}

.diff-dialog fieldset fieldset legend {
  display: none;
}

.diff-dialog div {
  padding: 0;
}

.diff-dialog div {
  padding: 0;
}

.diff-dialog div.variable-diffs {
  display: flex;
  flex-direction: column;
  align-items: center;
  /*justify-content: center;
  width: fit-content;
  max-width: 20vw;*/
  font-weight: 700;
  /*gap: 0.5rem;*/
  text-align: center;
  /*margin-right: 2vw;*/
  margin-bottom: 1.3rem;
}

.diff-dialog div.variable-unchanged {
  background: #f8f9fa;
  /* Colore di sfondo chiaro per differenziarlo */
  border-radius: 10px;
  /* Angoli arrotondati */
  padding: 1.5rem;
  /* Spazio interno */
  border: 1px solid #ddd;
  /* Bordo sottile */
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  /* Ombra leggera */
  text-align: center;
  /* Centrare il contenuto */
  margin-bottom: 1.5rem;
  /* Stesso margine inferiore per allinearlo */
}

.diff-dialog .unchanged-msg {
  font-size: 1rem;
  /* Dimensione del testo */
  color: #6c757d;
  /* Colore grigio per un aspetto più neutro */
  font-weight: 500;
  /* Leggermente in grassetto */
}

.diff-dialog div.variable-diffs .valueMessage > span:first-of-type {
  white-space: nowrap;
}

.diff-dialog div.variable-diffs .valueMessage {
  display: flex;
  align-items: center;
  gap: 12px;
}

.diff-dialog div.variable-diffs .valueMessage .varValue {
  max-width: 15vw;
  border: 1px solid #000;
  border-radius: 5px;
  padding: 0.5rem 0.75rem;
  text-align: center;
}

.diff-dialog div.variable-diff {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding-left: 9rem;
  padding-right: 9rem;
  padding-bottom: 3.3rem;
  padding-top: 1.5rem;
  height: auto;
  border: 1px solid #ddd;
  margin-bottom: 1.5rem;
  min-height: 250px;
}

.diff-dialog .buttons-container {
  display: flex;
  width: fit-content;
  position: sticky;
  bottom: 0;
  left: 100%;
  gap: 8px;
  z-index: 999;
}

.diff-dialog div.compareSets {
  padding: 1rem;
  margin: 1rem 1rem 1rem 0;
  border: 1px solid #000;
  border-radius: 5px;
  gap: 2rem;
}

.diff-dialog .setName {
  display: flex;
  white-space: nowrap;
  flex-direction: column;
  gap: 8px;
  padding: 1rem;
  border: 1px solid #000;
  border-radius: 5px;
}

.diff-dialog .setItemDiff {
  display: flex;
  margin-bottom: 2rem;
}

.diff-dialog .diffMessage {
  display: flex;
  align-items: center;
  margin-right: 2rem;
}

.diff-dialog .diffMessage span {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.diff-dialog .diffMessage span > strong {
  white-space: nowrap;
}

.close-confirm-dialog {
  padding: 1rem 2rem;
}

.close-confirm-dialog h3 {
  margin: 1rem 0 2rem 0;
}

.close-confirm-dialog .buttons-container {
  display: flex;
  justify-content: end;
  gap: 0.5rem;
}

.loading-import-dialog .loading-import-dialog-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
